import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import PurchaseOrderPlanningPage from './PurchaseOrderPlanningPage'
import LoadPos from '../components/multipo/LoadPos'
import SessionGuard from '../components/multipo/SessionGuard'
import '../styles/ui.css'
import '../styles/purchase-orders.css'

type Mode = 'load' | 'planner'

/**
 * BOSS MultiPO (docs/BossMultiPO.md): the web port of the legacy BOSS_PO tool, merged with the
 * PO Planner. Two modes, each behind its own permission: Load POs (boss.multipo) and Planner
 * (purchaseorders.create). The mode lives in the URL so links and refreshes land on it.
 */
export default function BossMultiPOPage({ popout = false }: { popout?: boolean }) {
    const token = useAuthStore(s => s.accessToken)
    const permissions = useAuthStore(s => s.user?.permissions) ?? []
    const canLoad = permissions.includes('boss.multipo')
    const canPlan = permissions.includes('purchaseorders.create')

    const [params, setParams] = useSearchParams()
    const requested = params.get('mode') === 'planner' ? 'planner' : 'load'
    // Fall back to whichever mode the user can open (the route guarantees at least one).
    const mode: Mode = requested === 'planner' ? (canPlan ? 'planner' : 'load') : (canLoad ? 'load' : 'planner')

    // Switching mode keeps the Load POs state (store, POs, view) in the URL.
    const setMode = (m: Mode) => {
        const next = new URLSearchParams(params)
        if (m === 'load') next.delete('mode')
        else next.set('mode', m)
        setParams(next, { replace: true })
    }

    // A pop-out window (docs/BossMultiPO.md §5) is Load POs only, without the site chrome.
    if (popout) {
        return (
            <div className="multipo-popout">
                <SessionGuard />
                <LoadPosMode token={token} popout />
            </div>
        )
    }

    return (
        <>
            <SessionGuard />
            <div className="page-header multipo-header">
                <div>
                    <h1>BOSS MultiPO</h1>
                    <p>
                        {mode === 'load'
                            ? 'Load one or more purchase orders on a catalog and enter quantities by colour and size.'
                            : 'Write purchase orders from a brand’s season catalog. Quantities are entered per colour and size, the way the order is actually placed.'}
                    </p>
                </div>
                {canLoad && canPlan && (
                    <div className="multipo-modes" role="tablist" aria-label="BOSS MultiPO mode">
                        <button role="tab" aria-selected={mode === 'load'} className={`multipo-mode ${mode === 'load' ? 'active' : ''}`} onClick={() => setMode('load')}>Load POs</button>
                        <button role="tab" aria-selected={mode === 'planner'} className={`multipo-mode ${mode === 'planner' ? 'active' : ''}`} onClick={() => setMode('planner')}>Planner</button>
                    </div>
                )}
            </div>

            {mode === 'planner' ? <PurchaseOrderPlanningPage embedded /> : <LoadPosMode token={token} />}
        </>
    )
}

/** Load POs mode. Cells save as they are entered once the PO writer login is set up. */
function LoadPosMode({ token, popout = false }: { token: string | null; popout?: boolean }) {
    const status = useQuery<{ writesEnabled: boolean }>({
        queryKey: ['multipo-status'],
        queryFn: () => api('/api/purchase-orders/multi/status', token),
    })

    return (
        <>
            {status.data && !status.data.writesEnabled && (
                <div className="multipo-notice">
                    Editing needs the PO writer login, which is not set up on the active connection profile.
                    Loading and viewing POs works without it.
                </div>
            )}
            <LoadPos token={token} writesEnabled={status.data?.writesEnabled ?? false} popout={popout} />
        </>
    )
}
